import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { isValidUUID, sanitizeUUID } from '@/lib/utils/uuidValidation';

// Use environment variables instead of hardcoded values
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing Supabase environment variables');
}

// Create the client with specific auth settings to improve session handling
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// Custom API endpoints
export const apiEndpoints = {
  customerSupport: `${SUPABASE_URL}/functions/v1/customer-support`,
};

// Type definitions for support chat features
export type SupportChat = {
  id: string;
  user_id: string | null;
  anonymous_session_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  is_anonymous: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  is_resolved: boolean;
}

export type SupportMessage = {
  id: string;
  chat_id: string;
  sender_type: 'user' | 'assistant' | 'admin';
  content: string;
  created_at: string;
  is_read: boolean;
}

// Custom interfaces for tables not included in auto-generated types
export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Voucher {
  id: string;
  code: string;
  type: 'coins' | 'premium' | 'credits';
  amount: number;
  is_used: boolean;
  user_id: string | null;
  created_at: string;
  used_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  profiles?: {
    username?: string;
    email?: string;
  };
}

export interface ConsentData {
  id: string;
  user_id: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  ip_address: string | null;
  created_at: string;
  updated_at: string;
  username?: string;
  email?: string;
}

// Type for the results returned by redeem_voucher function
export interface RedeemVoucherResult {
  success: boolean;
  message: string;
}

// Enhanced support service with session-based filtering for anonymous users
export const supportService = {
  async getChats() {
    try {
      console.log("supportService.getChats - Starting query");
      const { data, error } = await supabase
        .from('support_chats')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error("supportService.getChats - Database error:", error);
        throw error;
      }
      
      console.log(`supportService.getChats - Success: ${data?.length || 0} chats`);
      return { data, error: null };
    } catch (error) {
      console.error("supportService.getChats - Unexpected error:", error);
      return { data: null, error };
    }
  },

  async getChatMessages(chatId: string, anonymousSessionId?: string) {
    if (!isValidUUID(chatId)) {
      const error = new Error(`Invalid chat ID: ${chatId}`);
      console.error("supportService.getChatMessages - Invalid UUID:", chatId);
      return { data: null, error };
    }

    try {
      console.log(`supportService.getChatMessages - Fetching messages for chat: ${chatId}`);
      
      // First verify the chat belongs to the current user/session
      const { data: chatData, error: chatError } = await supabase
        .from('support_chats')
        .select('*')
        .eq('id', chatId)
        .single();
      
      if (chatError || !chatData) {
        console.error("supportService.getChatMessages - Chat not found:", chatError);
        return { data: null, error: chatError || new Error('Chat not found') };
      }
      
      // For anonymous chats, verify session matches
      if (chatData.is_anonymous && chatData.user_id === null) {
        if (!anonymousSessionId || chatData.anonymous_session_id !== anonymousSessionId) {
          console.error("supportService.getChatMessages - Anonymous session mismatch");
          return { data: null, error: new Error('Session mismatch') };
        }
      }
      
      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error("supportService.getChatMessages - Database error:", error);
        throw error;
      }
      
      console.log(`supportService.getChatMessages - Success: ${data?.length || 0} messages`);
      return { data, error: null };
    } catch (error) {
      console.error("supportService.getChatMessages - Unexpected error:", error);
      return { data: null, error };
    }
  },

  async createChat(userId: string | null, anonymousSessionId?: string, clientInfo?: any) {
    // For authenticated users
    if (userId) {
      const sanitizedUserId = sanitizeUUID(userId);
      if (!sanitizedUserId) {
        const error = new Error(`Invalid user ID: ${userId}`);
        console.error("supportService.createChat - Invalid user ID:", userId);
        return { data: null, error };
      }
    }

    try {
      const chatData: any = {
        is_anonymous: !userId,
        ip_address: clientInfo?.ipAddress || null,
        user_agent: clientInfo?.userAgent || null
      };

      if (userId) {
        chatData.user_id = userId;
        console.log(`supportService.createChat - Creating authenticated chat for user: ${userId}`);
      } else {
        chatData.anonymous_session_id = anonymousSessionId;
        console.log(`supportService.createChat - Creating anonymous chat with session: ${anonymousSessionId}`);
      }

      const { data, error } = await supabase
        .from('support_chats')
        .insert(chatData)
        .select()
        .single();
      
      if (error) {
        console.error("supportService.createChat - Database error:", error);
        throw error;
      }
      
      console.log(`supportService.createChat - Success: ${data?.id}`);
      return { data, error: null };
    } catch (error) {
      console.error("supportService.createChat - Unexpected error:", error);
      return { data: null, error };
    }
  },

  async findExistingChat(userId: string | null, anonymousSessionId?: string) {
    try {
      let query = supabase.from('support_chats').select('*').eq('is_resolved', false);
      
      if (userId) {
        const sanitizedUserId = sanitizeUUID(userId);
        if (!sanitizedUserId) return { data: null, error: null };
        query = query.eq('user_id', sanitizedUserId);
      } else if (anonymousSessionId) {
        // For anonymous users, filter by session ID after getting results
        query = query.eq('is_anonymous', true).is('user_id', null);
      } else {
        return { data: null, error: null };
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error("supportService.findExistingChat - Database error:", error);
        return { data: null, error };
      }
      
      // For anonymous users, filter by session ID
      if (!userId && anonymousSessionId && data) {
        const filteredData = data.filter(chat => 
          chat.anonymous_session_id === anonymousSessionId
        );
        return { data: filteredData?.[0] || null, error: null };
      }
      
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      console.error("supportService.findExistingChat - Unexpected error:", error);
      return { data: null, error };
    }
  },

  async createMessage(message: { chat_id: string; sender_type: 'user' | 'assistant' | 'admin'; content: string }, anonymousSessionId?: string) {
    const sanitizedChatId = sanitizeUUID(message.chat_id);
    
    if (!sanitizedChatId) {
      const error = new Error(`Invalid chat ID: ${message.chat_id}`);
      console.error("supportService.createMessage - Invalid chat ID:", message.chat_id);
      return { data: null, error };
    }

    if (!message.content || message.content.trim() === '') {
      const error = new Error("Message content cannot be empty");
      console.error("supportService.createMessage - Empty content");
      return { data: null, error };
    }

    try {
      // For anonymous users, verify chat ownership by session
      if (anonymousSessionId) {
        const { data: chatData, error: chatError } = await supabase
          .from('support_chats')
          .select('*')
          .eq('id', sanitizedChatId)
          .single();
        
        if (chatError || !chatData) {
          console.error("supportService.createMessage - Chat verification failed:", chatError);
          return { data: null, error: chatError || new Error('Chat not found') };
        }
        
        if (chatData.is_anonymous && chatData.anonymous_session_id !== anonymousSessionId) {
          console.error("supportService.createMessage - Session mismatch");
          return { data: null, error: new Error('Session mismatch') };
        }
      }

      console.log(`supportService.createMessage - Creating message for chat: ${sanitizedChatId}`);
      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          chat_id: sanitizedChatId,
          sender_type: message.sender_type,
          content: message.content.trim()
        })
        .select()
        .single();
      
      if (error) {
        console.error("supportService.createMessage - Database error:", error);
        throw error;
      }
      
      console.log(`supportService.createMessage - Success: ${data?.id}`);
      return { data, error: null };
    } catch (error) {
      console.error("supportService.createMessage - Unexpected error:", error);
      return { data: null, error };
    }
  },

  async updateChatResolution(chatId: string, isResolved: boolean) {
    const sanitizedChatId = sanitizeUUID(chatId);
    
    if (!sanitizedChatId) {
      const error = new Error(`Invalid chat ID: ${chatId}`);
      console.error("supportService.updateChatResolution - Invalid chat ID:", chatId);
      return { data: null, error };
    }

    try {
      console.log(`supportService.updateChatResolution - Updating chat ${sanitizedChatId} to resolved: ${isResolved}`);
      const { data, error } = await supabase
        .from('support_chats')
        .update({ is_resolved: isResolved })
        .eq('id', sanitizedChatId)
        .select()
        .single();
      
      if (error) {
        console.error("supportService.updateChatResolution - Database error:", error);
        throw error;
      }
      
      console.log(`supportService.updateChatResolution - Success: ${data?.id}`);
      return { data, error: null };
    } catch (error) {
      console.error("supportService.updateChatResolution - Unexpected error:", error);
      return { data: null, error };
    }
  }
}
